import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Gift, Users, Calendar, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  getReferralStats,
  applyReferralCode,
  copyToClipboard,
  generateReferralLink,
  validateReferralCodeFormat,
  type ReferralStats
} from '@/utils/referral-helpers';

interface RewardCode {
  id: string;
  promotion_code: string;
  discount_percent: number;
  status: string;
  reward_role: string;
  created_at: string;
  used_at: string | null;
}

export function PractitionerReferralSettings() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const { user } = useAuth();

  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [rewardCodes, setRewardCodes] = useState<RewardCode[]>([]);
  const [copiedRewardCode, setCopiedRewardCode] = useState<string | null>(null);

  // Load referral stats
  useEffect(() => {
    if (user?.id) {
      loadStats();
      loadRewardCodes();
    }
  }, [user?.id]);

  const loadStats = async () => {
    if (!user?.id) return;

    setLoading(true);
    const data = await getReferralStats(user.id, 'practitioner');
    setStats(data);
    setLoading(false);
  };

  const loadRewardCodes = async () => {
    if (!user?.id) return;
    const { data, error } = await (supabase
      .from('referral_reward_codes') as any)
      .select('id, promotion_code, discount_percent, status, reward_role, created_at, used_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (!error && data) {
      setRewardCodes(data as unknown as RewardCode[]);
    }
  };

  const handleCopyRewardCode = async (code: string) => {
    const success = await copyToClipboard(code);
    if (success) {
      setCopiedRewardCode(code);
      toast({ title: 'Code copied!', description: `${code} copied to clipboard.` });
      setTimeout(() => setCopiedRewardCode(null), 2000);
    }
  };

  // Handle copy referral code
  const handleCopyCode = async () => {
    if (!stats?.my_code) return;

    const success = await copyToClipboard(stats.my_code);
    if (success) {
      setCopied(true);
      toast({
        title: t('referral.codeCopied'),
        description: t('referral.codeCopiedDesc'),
      });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast({
        title: t('referral.copyFailed'),
        variant: 'destructive',
      });
    }
  };

  // Handle copy referral link
  const handleCopyLink = async () => {
    if (!stats?.my_code) return;

    const link = generateReferralLink(stats.my_code);
    const success = await copyToClipboard(link);

    if (success) {
      toast({
        title: t('referral.linkCopied'),
        description: t('referral.linkCopiedDesc'),
      });
    } else {
      toast({
        title: t('referral.copyFailed'),
        variant: 'destructive',
      });
    }
  };

  // Handle apply referral code
  const handleApplyCode = async () => {
    if (!user?.id || !inputCode.trim()) return;

    // Validate format
    if (!validateReferralCodeFormat(inputCode.toUpperCase())) {
      toast({
        title: t('referral.invalidFormat'),
        description: t('referral.invalidFormatDesc'),
        variant: 'destructive',
      });
      return;
    }

    setApplying(true);
    const result = await applyReferralCode(user.id, inputCode.trim(), 'practitioner');
    setApplying(false);

    if (result.success) {
      toast({
        title: t('referral.success'),
        description: result.message || t('referral.appliedSuccess'),
      });
      setInputCode('');
      // Reload stats
      await loadStats();
    } else {
      toast({
        title: t('referral.error'),
        description: result.error || t('referral.appliedError'),
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Your Referral Code Card */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl flex items-center gap-2 text-amber-900">
            <Gift className="h-5 w-5" />
            {t('referral.yourCode')}
          </CardTitle>
          <CardDescription className="text-sm text-amber-800">
            {t('referral.yourCodeDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-3 sm:p-6">
          {/* Referral Code Display */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="text-sm font-medium text-amber-900 mb-1">
                {t('referral.code')}
              </div>
              <div className="text-3xl font-bold tracking-wider font-mono bg-amber-100 text-amber-900 px-4 py-3 rounded-lg border border-amber-200">
                {stats?.my_code || 'XXXXXXXX'}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleCopyCode}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              disabled={!stats?.my_code}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t('referral.copied')}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {t('referral.copyCode')}
                </>
              )}
            </Button>
            <Button
              onClick={handleCopyLink}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              disabled={!stats?.my_code}
            >
              <Copy className="h-4 w-4 mr-2" />
              {t('referral.copyLink')}
            </Button>
          </div>

          {/* Stats */}
          <div className="pt-4 border-t border-amber-200 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <Users className="h-4 w-4" />
                {t('referral.totalReferrals')}
              </div>
              <div className="text-2xl font-bold text-amber-900">
                {stats?.total_referrals || 0}
              </div>
            </div>

            {/* Breakdown by user type */}
            {stats && (stats.client_referrals > 0 || stats.practitioner_referrals > 0) && (
              <div className="text-sm text-amber-800 pl-6 space-y-1">
                {stats.client_referrals > 0 && (
                  <div className="flex justify-between">
                    <span>{t('referral.clientReferrals')}</span>
                    <span className="font-medium">{stats.client_referrals}</span>
                  </div>
                )}
                {stats.practitioner_referrals > 0 && (
                  <div className="flex justify-between">
                    <span>{t('referral.practitionerReferrals')}</span>
                    <span className="font-medium">{stats.practitioner_referrals}</span>
                  </div>
                )}
              </div>
            )}

            {/* List of referred users */}
            {stats && stats.total_referrals > 0 && (
              <div className="pt-3 border-t border-amber-200">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-900 mb-2">
                  <Users className="h-4 w-4" />
                  {t('referral.peopleReferred')}
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {/* Client referrals */}
                  {stats.referred_clients?.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 bg-amber-100/50 rounded text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center font-medium text-xs">
                          {user.first_name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div>
                          <div className="font-medium text-amber-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-amber-700">
                            {t('referral.clientLabel')}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-amber-700">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  {/* Practitioner referrals */}
                  {stats.referred_practitioners?.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 bg-amber-100/50 rounded text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center font-medium text-xs">
                          {user.first_name?.charAt(0)?.toUpperCase() || 'P'}
                        </div>
                        <div>
                          <div className="font-medium text-amber-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-amber-700">
                            {t('referral.practitionerLabel')}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-amber-700">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Apply Referral Code Card */}
      {stats?.can_apply_code ? (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <Calendar className="h-5 w-5 text-amber-600" />
              {t('referral.enterCode')}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {t('referral.enterCodeDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-amber-200 bg-amber-50/50">
              <AlertDescription className="text-amber-900">
                {t('referral.oneTimeOnly')}
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Input
                placeholder="ABC12345"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                maxLength={8}
                className="font-mono uppercase border-amber-200 focus-visible:ring-amber-500"
                disabled={applying}
              />
              <Button
                onClick={handleApplyCode}
                disabled={!inputCode.trim() || applying}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {applying ? t('referral.applying') : t('referral.apply')}
              </Button>
            </div>

            {/* Time Window Info */}
            {stats?.days_since_registration !== undefined && (
              <p className="text-sm text-amber-800">
                {t('referral.timeWindow', {
                  days: Math.max(0, 7 - Math.floor(stats.days_since_registration))
                })}
              </p>
            )}
          </CardContent>
        </Card>
      ) : stats?.applied_code ? (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-900">
              <Check className="h-5 w-5 text-green-600" />
              {t('referral.alreadyApplied')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className="text-green-700">
                {t('referral.appliedCode')}:
              </span>
              <span className="text-xl font-bold font-mono text-green-900">
                {stats.applied_code}
              </span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-gray-300">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600 text-center">
              {t('referral.windowExpired')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reward Codes Card */}
      {rewardCodes.length > 0 && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2 text-green-900">
              <Ticket className="h-5 w-5" />
              Your Reward Codes
            </CardTitle>
            <CardDescription className="text-sm text-green-800">
              Discount codes earned from referrals. Enter these at checkout to apply.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 p-3 sm:p-6">
            {rewardCodes.map((rc) => (
              <div
                key={rc.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  rc.status === 'active'
                    ? 'bg-green-100/60 border-green-300'
                    : 'bg-gray-100/60 border-gray-200'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-lg font-bold font-mono tracking-wider ${
                      rc.status === 'active' ? 'text-green-900' : 'text-gray-500 line-through'
                    }`}>
                      {rc.promotion_code}
                    </span>
                    <Badge variant={rc.status === 'active' ? 'default' : 'secondary'}
                      className={rc.status === 'active' ? 'bg-green-600' : ''}>
                      {rc.status === 'active' ? 'Active' : 'Used'}
                    </Badge>
                  </div>
                  <div className={`text-xs mt-1 ${rc.status === 'active' ? 'text-green-700' : 'text-gray-500'}`}>
                    {rc.discount_percent}% off · {rc.reward_role === 'referrer' ? 'Referrer reward' : 'Referral reward'} · {new Date(rc.created_at).toLocaleDateString()}
                  </div>
                </div>
                {rc.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyRewardCode(rc.promotion_code)}
                    className="ml-2 border-green-300 text-green-700 hover:bg-green-200 shrink-0"
                  >
                    {copiedRewardCode === rc.promotion_code ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
